import React, { useEffect, useState } from 'react';
import { apiService } from '../../services/api.service';

interface AdminMaintenancePageProps {
    enabled: boolean | null;
    statusError: string;
    onRefresh: () => Promise<void>;
    onChange: (enabled: boolean) => void;
    skinCollabEventsEnabled: boolean;
    onSkinCollabEventsChange: (enabled: boolean) => void;
}

const AdminMaintenancePage: React.FC<AdminMaintenancePageProps> = ({
    enabled,
    statusError,
    onRefresh,
    onChange,
    skinCollabEventsEnabled,
    onSkinCollabEventsChange,
}) => {
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [shopCookie, setShopCookie] = useState('');
    const [shopCsrfToken, setShopCsrfToken] = useState('');
    const [isCookieConfigured, setIsCookieConfigured] = useState(false);
    const [isLoadingCookieStatus, setIsLoadingCookieStatus] = useState(true);
    const [isSavingCookie, setIsSavingCookie] = useState(false);
    const [cookieMessage, setCookieMessage] = useState('');
    const [cookieError, setCookieError] = useState('');
    const [shopJobAction, setShopJobAction] = useState<'submit' | 'check' | null>(null);
    const [shopJobMessage, setShopJobMessage] = useState('');
    const [shopJobError, setShopJobError] = useState('');
    const [shopJobsEnabled, setShopJobsEnabled] = useState<boolean | null>(null);
    const [isLoadingShopJobs, setIsLoadingShopJobs] = useState(true);
    const [isSavingShopJobs, setIsSavingShopJobs] = useState(false);
    const [shopJobsStatusError, setShopJobsStatusError] = useState('');
    const [shopJobsStatusMessage, setShopJobsStatusMessage] = useState('');
    const [isSavingSkinCollabEvents, setIsSavingSkinCollabEvents] = useState(false);
    const [skinCollabEventsMessage, setSkinCollabEventsMessage] = useState('');
    const [skinCollabEventsError, setSkinCollabEventsError] = useState('');

    useEffect(() => {
        let isMounted = true;
        apiService.get<{ enabled: boolean }>('/system-settings/shop-jobs')
            .then((response) => {
                if (isMounted) setShopJobsEnabled(response.enabled);
            })
            .catch((requestError: any) => {
                if (isMounted) {
                    setShopJobsStatusError(requestError.response?.data?.message || 'Không thể tải trạng thái job shop.');
                }
            })
            .finally(() => {
                if (isMounted) setIsLoadingShopJobs(false);
            });
        return () => { isMounted = false; };
    }, []);

    const refreshShopJobsStatus = async () => {
        setIsLoadingShopJobs(true);
        setShopJobsStatusError('');
        setShopJobsStatusMessage('');
        try {
            const response = await apiService.get<{ enabled: boolean }>('/system-settings/shop-jobs');
            setShopJobsEnabled(response.enabled);
        } catch (requestError: any) {
            setShopJobsEnabled(null);
            setShopJobsStatusError(requestError.response?.data?.message || 'Không thể tải trạng thái job shop.');
        } finally {
            setIsLoadingShopJobs(false);
        }
    };

    const toggleShopJobs = async () => {
        if (shopJobsEnabled === null || isSavingShopJobs || isLoadingShopJobs) return;
        setIsSavingShopJobs(true);
        setShopJobsStatusError('');
        setShopJobsStatusMessage('');
        try {
            const response = await apiService.patch<{ enabled: boolean }>('/system-settings/shop-jobs', {
                enabled: !shopJobsEnabled,
            });
            setShopJobsEnabled(response.enabled);
            setShopJobsStatusMessage(response.enabled
                ? 'Đã bật job shop tự động. Job sẽ tiếp tục ở lượt chạy kế tiếp.'
                : 'Đã tắt job shop tự động. Lượt đang chạy sẽ hoàn thành; các lượt tiếp theo sẽ tạm dừng.');
        } catch (requestError: any) {
            setShopJobsStatusError(requestError.response?.data?.message || 'Không thể cập nhật trạng thái job shop.');
        } finally {
            setIsSavingShopJobs(false);
        }
    };

    useEffect(() => {
        let isMounted = true;
        apiService.get<{ configured: boolean; cookie: string; csrfToken: string }>('/system-settings/shop-cookie')
            .then((response) => {
                if (isMounted) {
                    setIsCookieConfigured(response.configured);
                    setShopCookie(response.cookie);
                    setShopCsrfToken(response.csrfToken);
                }
            })
            .catch((requestError: any) => {
                if (isMounted) {
                    setCookieError(requestError.response?.data?.message || 'Không thể tải trạng thái cookie.');
                }
            })
            .finally(() => {
                if (isMounted) setIsLoadingCookieStatus(false);
            });

        return () => {
            isMounted = false;
        };
    }, []);

    const toggleMaintenance = async () => {
        if (enabled === null) return;
        setIsSaving(true);
        setMessage('');
        setError('');
        try {
            const response = await apiService.patch<{ enabled: boolean }>('/system-settings/maintenance', {
                enabled: !enabled,
            });
            onChange(response.enabled);
            setMessage(response.enabled
                ? 'Đã bật chế độ bảo trì. Khách hàng không thể tạo đơn hàng hoặc nạp tiền.'
                : 'Đã tắt chế độ bảo trì. Khách hàng có thể tạo đơn hàng và nạp tiền trở lại.');
        } catch (requestError: any) {
            setError(requestError.response?.data?.message || 'Không thể cập nhật chế độ bảo trì.');
        } finally {
            setIsSaving(false);
        }
    };

    const toggleSkinCollabEvents = async () => {
        setIsSavingSkinCollabEvents(true);
        setSkinCollabEventsError('');
        setSkinCollabEventsMessage('');
        try {
            const response = await apiService.patch<{ enabled: boolean }>('/system-settings/skin-collab-events', {
                enabled: !skinCollabEventsEnabled,
            });
            onSkinCollabEventsChange(response.enabled);
            setSkinCollabEventsMessage(response.enabled
                ? 'Đã hiện tab quay skin hợp tác cho User và Partner.'
                : 'Đã ẩn tab quay skin hợp tác với User và Partner.');
        } catch (requestError: any) {
            setSkinCollabEventsError(requestError.response?.data?.message || 'Không thể cập nhật hiển thị sự kiện.');
        } finally {
            setIsSavingSkinCollabEvents(false);
        }
    };

    const saveShopCookie = async () => {
        if (!shopCookie.trim() || !shopCsrfToken.trim()) {
            setCookieError('Vui lòng nhập cả cookie và CSRF token.');
            setCookieMessage('');
            return;
        }

        setIsSavingCookie(true);
        setCookieMessage('');
        setCookieError('');
        try {
            const response = await apiService.patch<{ configured: boolean }>('/system-settings/shop-cookie', {
                cookie: shopCookie,
                csrfToken: shopCsrfToken,
            });
            setIsCookieConfigured(response.configured);
            setCookieMessage('Đã lưu cookie và CSRF token.');
        } catch (requestError: any) {
            setCookieError(requestError.response?.data?.message || 'Không thể lưu cookie.');
        } finally {
            setIsSavingCookie(false);
        }
    };

    const runShopJob = async (action: 'submit' | 'check') => {
        setShopJobAction(action);
        setShopJobMessage('');
        setShopJobError('');
        try {
            if (action === 'submit') {
                const response = await apiService.post<{ data: { submitted: number } }>(
                    '/orders/shop-integration/test-submit-pending',
                );
                setShopJobMessage(`Đã gửi ${response.data.submitted} order pending lên shop.`);
            } else {
                const response = await apiService.post<{
                    data: { completed: number; running: number; invalid: number; notFound: number };
                }>('/orders/shop-integration/test-check-submitted');
                const { completed, running, invalid, notFound } = response.data;
                setShopJobMessage(
                    `Hoàn tất: ${completed}; đang chạy: ${running}; mã lỗi: ${invalid}; chưa thấy trên shop: ${notFound}.`,
                );
            }
        } catch (requestError: any) {
            setShopJobError(requestError.response?.data?.message || 'Không thể chạy kiểm tra shop.');
        } finally {
            setShopJobAction(null);
        }
    };

    return (
        <div className="flex-1 overflow-y-auto bg-gray-50 p-4 md:p-8">
            <div className="mx-auto max-w-3xl space-y-5">
                <div>
                    <p className="text-sm font-medium text-blue-600">Quản trị hệ thống</p>
                    <h2 className="mt-1 text-2xl font-semibold text-gray-900">Chế độ bảo trì</h2>
                    <p className="mt-1 text-sm text-gray-500">
                        Khách hàng vẫn xem đơn hàng và sử dụng các chức năng khác, nhưng không thể tạo đơn mới hoặc nạp tiền.
                    </p>
                </div>

                {statusError && (
                    <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">
                        <span>{statusError}</span>
                        <button
                            type="button"
                            onClick={() => void onRefresh()}
                            className="rounded-lg border border-red-200 px-3 py-1.5 font-semibold hover:bg-red-100"
                        >
                            Tải lại trạng thái
                        </button>
                    </div>
                )}
                {(message || error) && (
                    <div role={error ? 'alert' : 'status'} className={`rounded-xl px-4 py-3 text-sm ${error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                        {error || message}
                    </div>
                )}

                <section className="flex flex-wrap items-center justify-between gap-5 rounded-2xl bg-white p-5 shadow-sm">
                    <div>
                        <p className="text-sm text-gray-500">Trạng thái hiện tại</p>
                        <p className={`mt-1 text-xl font-semibold ${enabled ? 'text-amber-700' : enabled === false ? 'text-green-700' : 'text-gray-500'}`}>
                            {enabled === null ? 'Chưa tải được trạng thái' : enabled ? 'Đang bảo trì' : 'Đang hoạt động'}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => void toggleMaintenance()}
                        disabled={enabled === null || isSaving}
                        className={`rounded-xl px-5 py-3 font-semibold text-white transition disabled:cursor-wait disabled:opacity-50 ${enabled ? 'bg-green-600 hover:bg-green-700' : 'bg-amber-600 hover:bg-amber-700'}`}
                    >
                        {isSaving ? 'Đang cập nhật...' : enabled ? 'Tắt bảo trì' : 'Bật bảo trì'}
                    </button>
                </section>

                <section className="flex flex-wrap items-center justify-between gap-5 rounded-2xl bg-white p-5 shadow-sm">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Sự kiện quay skin hợp tác</h3>
                        <p className="mt-1 text-sm text-gray-500">Tắt để ẩn tab với User và Partner; Admin vẫn xem và quản lý được.</p>
                        <p className={`mt-2 text-sm font-semibold ${skinCollabEventsEnabled ? 'text-green-700' : 'text-gray-500'}`}>
                            {skinCollabEventsEnabled ? 'Đang hiển thị' : 'Đang ẩn'}
                        </p>
                    </div>
                    <button
                        type="button"
                        role="switch"
                        aria-label="Hiển thị sự kiện quay skin hợp tác"
                        aria-checked={skinCollabEventsEnabled}
                        onClick={() => void toggleSkinCollabEvents()}
                        disabled={isSavingSkinCollabEvents}
                        className={`rounded-xl px-5 py-3 font-semibold text-white transition disabled:cursor-wait disabled:opacity-50 ${skinCollabEventsEnabled ? 'bg-amber-600 hover:bg-amber-700' : 'bg-green-600 hover:bg-green-700'}`}
                    >
                        {isSavingSkinCollabEvents ? 'Đang cập nhật...' : skinCollabEventsEnabled ? 'Ẩn sự kiện' : 'Hiện sự kiện'}
                    </button>
                    {(skinCollabEventsError || skinCollabEventsMessage) && (
                        <div role={skinCollabEventsError ? 'alert' : 'status'} className={`w-full rounded-xl px-4 py-3 text-sm ${skinCollabEventsError ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                            {skinCollabEventsError || skinCollabEventsMessage}
                        </div>
                    )}
                </section>

                <section className="space-y-4 rounded-2xl bg-white p-5 shadow-sm">
                    <div className="flex flex-wrap items-center justify-between gap-5">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Job shop tự động</h3>
                            <p className="mt-1 text-sm text-gray-500">
                                Tự động gửi đơn pending, kiểm tra đơn đã gửi và gửi acc clone lên shop.
                            </p>
                            <p className={`mt-2 text-sm font-semibold ${shopJobsEnabled === true ? 'text-green-700' : shopJobsEnabled === false ? 'text-amber-700' : 'text-gray-500'}`}>
                                {isLoadingShopJobs ? 'Đang tải trạng thái...' : shopJobsEnabled === null ? 'Chưa tải được trạng thái' : shopJobsEnabled ? 'Đang bật' : 'Đang tắt'}
                            </p>
                        </div>
                        <button
                            type="button"
                            role="switch"
                            aria-label="Job shop tự động"
                            aria-checked={shopJobsEnabled === true}
                            onClick={() => void toggleShopJobs()}
                            disabled={shopJobsEnabled === null || isLoadingShopJobs || isSavingShopJobs}
                            className={`rounded-xl px-5 py-3 font-semibold text-white transition disabled:cursor-wait disabled:opacity-50 ${shopJobsEnabled ? 'bg-amber-600 hover:bg-amber-700' : 'bg-green-600 hover:bg-green-700'}`}
                        >
                            {isSavingShopJobs ? 'Đang cập nhật...' : shopJobsEnabled ? 'Tắt job' : 'Bật job'}
                        </button>
                    </div>
                    <p className="text-sm text-gray-500">
                        Khi tắt, lượt đang chạy sẽ hoàn thành. Bạn vẫn có thể chạy kiểm tra thủ công bên dưới.
                    </p>
                    {(shopJobsStatusError || shopJobsStatusMessage) && (
                        <div role={shopJobsStatusError ? 'alert' : 'status'} className={`rounded-xl px-4 py-3 text-sm ${shopJobsStatusError ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                            {shopJobsStatusError || shopJobsStatusMessage}
                        </div>
                    )}
                    <button
                        type="button"
                        onClick={() => void refreshShopJobsStatus()}
                        disabled={isLoadingShopJobs || isSavingShopJobs}
                        className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                        Tải lại trạng thái job
                    </button>
                </section>

                <section className="space-y-4 rounded-2xl bg-white p-5 shadow-sm">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Cookie tích hợp shop</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            {isLoadingCookieStatus
                                ? 'Đang kiểm tra trạng thái cookie...'
                                : isCookieConfigured
                                    ? 'Đã cấu hình cookie và token. Bạn có thể xem hoặc chỉnh sửa bên dưới.'
                                    : 'Chưa cấu hình cookie và token.'}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                            Cookie và CSRF token hiện tại được hiển thị để admin có thể xem và chỉnh sửa.
                        </p>
                    </div>

                    {(cookieMessage || cookieError) && (
                        <div role={cookieError ? 'alert' : 'status'} className={`rounded-xl px-4 py-3 text-sm ${cookieError ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                            {cookieError || cookieMessage}
                        </div>
                    )}

                    <label className="block">
                        <span className="mb-1 block text-sm font-medium text-gray-700">Cookie hiện tại</span>
                        <textarea
                            value={shopCookie}
                            onChange={(event) => setShopCookie(event.target.value)}
                            maxLength={20000}
                            placeholder="Dán cookie đăng nhập tại đây"
                            rows={4}
                            spellCheck={false}
                            className="w-full resize-y rounded-xl border border-gray-300 px-4 py-3 font-mono text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        />
                    </label>
                    <label className="block">
                        <span className="mb-1 block text-sm font-medium text-gray-700">CSRF token (x-csrf-token)</span>
                        <textarea
                            value={shopCsrfToken}
                            onChange={(event) => setShopCsrfToken(event.target.value)}
                            maxLength={4000}
                            placeholder="Dán giá trị x-csrf-token tại đây"
                            rows={2}
                            spellCheck={false}
                            className="w-full resize-y rounded-xl border border-gray-300 px-4 py-3 font-mono text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        />
                    </label>
                    <button
                        type="button"
                        onClick={() => void saveShopCookie()}
                        disabled={isSavingCookie || isLoadingCookieStatus}
                        className="rounded-xl bg-blue-600 px-5 py-3 font-semibold text-white transition hover:bg-blue-700 disabled:cursor-wait disabled:opacity-50"
                    >
                        {isSavingCookie ? 'Đang lưu...' : isCookieConfigured ? 'Cập nhật cookie' : 'Lưu cookie'}
                    </button>
                </section>

                <section className="space-y-4 rounded-2xl bg-white p-5 shadow-sm">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Kiểm tra tích hợp shop</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            Chạy thủ công để kiểm tra gửi đơn pending hoặc đồng bộ trạng thái các mã đã gửi.
                        </p>
                    </div>

                    {(shopJobMessage || shopJobError) && (
                        <div role={shopJobError ? 'alert' : 'status'} className={`rounded-xl px-4 py-3 text-sm ${shopJobError ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                            {shopJobError || shopJobMessage}
                        </div>
                    )}

                    <div className="flex flex-wrap gap-3">
                        <button
                            type="button"
                            onClick={() => void runShopJob('submit')}
                            disabled={!isCookieConfigured || isLoadingCookieStatus || shopJobAction !== null}
                            className="rounded-xl bg-blue-600 px-5 py-3 font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {shopJobAction === 'submit' ? 'Đang gửi...' : 'Test gửi order pending'}
                        </button>
                        <button
                            type="button"
                            onClick={() => void runShopJob('check')}
                            disabled={!isCookieConfigured || isLoadingCookieStatus || shopJobAction !== null}
                            className="rounded-xl bg-indigo-600 px-5 py-3 font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {shopJobAction === 'check' ? 'Đang kiểm tra...' : 'Test kiểm tra trạng thái'}
                        </button>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default AdminMaintenancePage;
